import { ensureFreshAccessToken } from '../../shared/supabase/supabaseClient';
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent';
import { messageFromInvoke, throwIfInvokeError } from '../../shared/supabase/invokeErrorUtils';
import type { InvokeErrorPayload } from '../../shared/supabase/invokeErrorUtils';

export type CustomerPaymentQrCreateResponse = {
  qrData: string | null;
  orderId: string | null;
  status: string;
  amount: number;
  expiresAt: string | null;
  qrAttempt: number | null;
};

export type CustomerPaymentQrStatusResponse = {
  ok: boolean;
  status: string;
  amount: number | null;
  qrData: string | null;
  orderId: string | null;
  mpPaymentId: string | null;
  qrAttempt: number | null;
  expiresAt: string | null;
  movementId: string | null;
  settleError: string | null;
};

async function authHeaders(): Promise<Record<string, string>> {
  const token = await ensureFreshAccessToken();
  if (!token) throw new Error('Sesión requerida. Iniciá sesión de nuevo.');
  return { Authorization: `Bearer ${token}` };
}

/** Genera (o regenera) el QR de Mercado Pago para un intent de cobro de cuenta. */
export async function createMercadoPagoQrForCustomerPayment(
  intentId: string,
  options?: { regenerate?: boolean }
): Promise<CustomerPaymentQrCreateResponse> {
  const headers = await authHeaders();

  const { data, error, presented } = await invokeEdgeOrPresent<
    InvokeErrorPayload & CustomerPaymentQrCreateResponse
  >(
    'mercadopago-customer-qr',
    {
      body: {
        intentId: intentId.trim(),
        regenerate: options?.regenerate === true,
      },
      headers,
    },
    {
      module: 'mercadopago',
      op: 'mp.customer_qr_create',
      fallbackMessage: 'Error al generar QR en Mercado Pago',
    },
  );
  if (error) {
    if (presented) {
      throw error instanceof Error
        ? error
        : new Error(messageFromInvoke(data, error, 'Error al generar QR en Mercado Pago'));
    }
    throw new Error(messageFromInvoke(data, error, 'Error al generar QR en Mercado Pago'));
  }
  throwIfInvokeError(data);
  return (data ?? { qrData: null, orderId: null, status: 'pending', amount: 0, expiresAt: null, qrAttempt: null }) as CustomerPaymentQrCreateResponse;
}

/** Poll del estado del cobro QR — sincroniza con MP si sigue pendiente (fallback del webhook). */
export const getCustomerPaymentQrStatus = async (intentId: string): Promise<CustomerPaymentQrStatusResponse> => {
  const headers = await authHeaders();
  const { data, error, presented } = await invokeEdgeOrPresent('get-customer-payment-qr', {
    body: { intentId: intentId.trim() },
    headers,
  }, {
    module: 'mercadopago',
    op: 'mp.get_customer_payment_qr',
    fallbackMessage: 'Error al consultar el cobro QR',
  });
  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'Error al consultar el cobro QR');
    }
    throw new Error(error.message || 'Error al consultar el cobro QR');
  }
  const body = (data ?? {}) as CustomerPaymentQrStatusResponse & { error?: string };
  if (body?.error) throw new Error(body.error);
  return body;
};
